/**
 * System-notifications polling context (DNKADM-0047 U1; v2 re-point DNKADM-0062).
 *
 * A single shared hook/context (NOT a per-render fetch) that loads the actionable
 * notification set on mount and POLLS it on a fixed 60s interval, so the app-bar
 * bell (U1) and the popover list (U2) read one shared, fresh list. The DNKADM-0062
 * re-point carries this machinery over UNCHANGED — it calls `getNotifications()`,
 * which now hits `/v2/admin/notifications` (handoff §5.20); the 60s cadence, badge
 * counts, and visibility catch-up logic are identical. It mounts inside the
 * authenticated shell (`AppLayout`) where the JWT exists.
 *
 * FAIL-SOFT by design: a load error never throws into the tree and never breaks the
 * app bar — it lands in `status: 'error'` with the last-known list left intact, so
 * the bell simply shows no new badge. The badge consumer hides itself at count 0.
 *
 * Polling pauses while the tab is hidden (no point refreshing an unseen badge) and
 * fires an immediate catch-up refresh on becoming visible again.
 *
 * Scope note: U1 ships the load+poll and the counts the bell badges. Per
 * DNKADM-0243 the attention count is the server's actionable `totalElements` (the
 * list length when it sends none), since a bounded page undercounts; the OPEN-ERROR
 * emphasis is still derived from the loaded rows.
 * U2 will consume the same context (the list + a refresh after an acknowledge) so the
 * badge and popover stay in sync from one source of truth — hence the shared `refresh`
 * already exposed here.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getNotifications, type Notification } from '@/api/notifications';
import {
  NotificationsContext,
  POLL_INTERVAL_MS,
  type NotificationsContextValue,
  type NotificationsStatus,
} from './notificationsContextValue';

export interface NotificationsProviderProps {
  children: ReactNode;
  /**
   * Load and poll the feed. Defaults to `true` — the shell passes `false` for a
   * STAFF session (DNKADM-0359), for which `/v2/admin/notifications` is `403`
   * (dink-api HANDOFF §2) and the bell is not rendered: polling a guaranteed
   * `403` every 60s is noise, not resilience. Disabled, the context still
   * mounts with an empty, `ready` feed so any consumer reads a valid value
   * instead of throwing. Fixed for the life of a session in practice (it is
   * derived from the token's tier).
   */
  enabled?: boolean;
}

export function NotificationsProvider({ children, enabled = true }: NotificationsProviderProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // The server's `totalElements` for the actionable set — the honest badge count
  // behind a bounded page (DNKADM-0243); null when the server sent no total.
  const [total, setTotal] = useState<number | null>(null);
  const [status, setStatus] = useState<NotificationsStatus>(enabled ? 'loading' : 'ready');

  // Guard against committing a stale in-flight load (last-call-wins) and against
  // setting state after unmount, mirroring LocationContext/useResource.
  const mountedRef = useRef(true);
  const runIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    void (async () => {
      try {
        // Default actionable set (OPEN + ACKNOWLEDGED) — the wrapper omits `status`.
        const page = await getNotifications();
        if (!mountedRef.current || runIdRef.current !== runId) return;
        setNotifications(page.items);
        setTotal(page.total);
        setStatus('ready');
      } catch {
        if (!mountedRef.current || runIdRef.current !== runId) return;
        // FAIL-SOFT: keep the last-known list; the bell just shows no fresh badge
        // and the app bar is untouched. A notifications outage is never fatal.
        setStatus('error');
      }
    })();
  }, []);

  // Load on mount, then poll on the interval. The interval is paused while the tab
  // is hidden and resumed (with an immediate catch-up load) on becoming visible.
  useEffect(() => {
    // Disabled (a STAFF session, DNKADM-0359): no load, no interval, no
    // visibility listener — the feed stays the empty `ready` initial state.
    if (!enabled) return;

    let timer: ReturnType<typeof setInterval> | undefined;

    const startPolling = () => {
      if (timer !== undefined) return;
      timer = setInterval(load, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (timer !== undefined) {
        clearInterval(timer);
        timer = undefined;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopPolling();
      } else {
        load(); // catch up immediately on return
        startPolling();
      }
    };

    load();
    if (document.visibilityState !== 'hidden') startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load, enabled]);

  // The badge count is the server's actionable `totalElements` when it sent one —
  // the page is bounded, so counting the rows we happen to hold would undercount
  // (DNKADM-0243). Falls back to the loaded list length when there is no total.
  const openCount = total ?? notifications.length;
  const openErrorCount = useMemo(
    () => notifications.filter((n) => n.status === 'OPEN' && n.severity === 'ERROR').length,
    [notifications],
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({ notifications, openCount, openErrorCount, status, refresh: load }),
    [notifications, openCount, openErrorCount, status, load],
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}
